import { useState } from "react";
import { motion } from "framer-motion";
import { Flag, Images } from "lucide-react";
import { timeline, teamInfo } from "../../data/arsenalData";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export const History = () => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [galleryUrls, setGalleryUrls] = useState([]);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const openChampionshipGallery = ({ title, urls }) => {
    setGalleryTitle(title);
    setGalleryUrls(urls);
    setGalleryOpen(true);
    setLightboxUrl(null);
  };

  return (
    <section
      id="historia"
      data-testid="history-section"
      className="relative bg-[#F8F9FA] text-[#061930] py-24 md:py-32 overflow-hidden"
    >
      <Dialog open={galleryOpen} onOpenChange={(open) => !open && setGalleryOpen(false)}>
        <DialogContent className="max-h-[95vh] max-w-[min(96vw,900px)] overflow-y-auto border border-[#DB0007]/30 bg-[#061930] p-4 text-white sm:p-6">
          <DialogTitle className="font-anton uppercase text-xl tracking-tight text-white pr-8">
            {galleryTitle}
          </DialogTitle>
          <p className="font-outfit text-sm text-white/70 -mt-1 mb-3">Momentos del campeonato</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
            {galleryUrls.map((url, i) => (
              <button
                key={url}
                type="button"
                className="relative aspect-[4/3] overflow-hidden border border-white/10 bg-[#0a2240] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DB0007]"
                onClick={() => setLightboxUrl(url)}
                data-testid={`history-gallery-thumb-${i + 1}`}
              >
                <img src={url} alt={`${galleryTitle} · ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxUrl != null} onOpenChange={(open) => !open && setLightboxUrl(null)}>
        <DialogContent className="max-h-[95vh] max-w-[min(96vw,1200px)] border border-[#DB0007]/30 bg-[#061930] p-3 text-white sm:p-4">
          <DialogTitle className="sr-only">Vista ampliada</DialogTitle>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="Vista ampliada"
              className="mx-auto max-h-[min(85vh,900px)] w-full max-w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Decorative red strip */}
      <div className="absolute top-0 left-0 w-1/3 h-1 bg-[#DB0007]" />
      <div className="absolute top-1 left-0 w-1/5 h-[2px] bg-[#9C824A]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="section-overline">Nuestra Historia</div>
            <h2 className="font-anton uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight mt-6">
              Una <span className="text-[#DB0007]">década</span> de
              <br />
              pasión arsenalista
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="md:col-span-7 md:pt-16"
          >
            <p className="font-outfit text-base sm:text-lg text-[#061930]/70 leading-relaxed">
              Lo que empezó como un sueño compartido entre un grupo de amigos en Allende,
              Nuevo León, hoy es una institución que representa a toda una comunidad.
              Cada año escrito en nuestra historia es un año de sudor, amistad y victoria.
            </p>
            <div className="mt-8 overflow-hidden border-l-4 border-[#9C824A] pl-6">
              <img
                src={teamInfo.teamPhoto}
                alt="Plantilla Arsenal de Allende"
                className="w-full h-auto"
              />
              <p className="font-mono-jb text-xs text-[#061930]/60 mt-3 uppercase tracking-wider">
                → Plantel temporada 2026 · Allende, N.L.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line (desktop) */}
          <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[2px] bg-[#061930]/15" />

          <div className="space-y-12 md:space-y-24">
            {timeline.map((item, idx) => (
              <TimelineItem
                key={`${item.year}-${idx}`}
                item={item}
                index={idx}
                onOpenChampionshipGallery={openChampionshipGallery}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TimelineItem = ({ item, index, onOpenChampionshipGallery }) => {
  const isLeft = index % 2 === 0;
  const hasGallery = Array.isArray(item.galleryImages) && item.galleryImages.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="relative md:grid md:grid-cols-2 md:gap-12 items-center"
      data-testid={`timeline-item-${item.year}`}
    >
      {/* Dot */}
      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#DB0007] border-4 border-[#F8F9FA] items-center justify-center shadow-[0_0_0_4px_rgba(219,0,7,0.15)] z-10">
        <Flag size={14} className="text-white" />
      </div>

      {/* Left content or empty */}
      <div className={`${isLeft ? "md:text-right md:pr-12" : "md:order-2 md:pl-12"}`}>
        <div className="font-anton text-6xl md:text-8xl text-[#DB0007] leading-none">
          {item.year}
        </div>
        <h3 className="font-anton uppercase text-2xl md:text-3xl mt-3 tracking-tight">
          {item.title}
        </h3>
        <p className="font-outfit text-[#061930]/70 mt-3 max-w-md md:max-w-full leading-relaxed">
          {item.description}
        </p>
        {hasGallery && (
          <div className={`mt-4 ${isLeft ? "md:flex md:justify-end" : ""}`}>
            <button
              type="button"
              data-testid="history-2025-gallery-open"
              onClick={() =>
                onOpenChampionshipGallery({
                  title: `${item.title} · ${item.year}`,
                  urls: item.galleryImages,
                })
              }
              className="inline-flex items-center gap-2 border border-[#DB0007] bg-white px-4 py-2.5 font-outfit text-sm font-semibold uppercase tracking-wider text-[#DB0007] transition-colors hover:bg-[#DB0007] hover:text-white"
            >
              <Images size={18} aria-hidden />
              {item.galleryLinkLabel ?? "Ver galería"}
            </button>
          </div>
        )}
        <div className={`mt-5 h-[2px] w-16 bg-[#9C824A] ${isLeft ? "md:ml-auto" : ""}`} />
      </div>
      {/* spacer (opposite column) */}
      <div className={isLeft ? "" : "md:order-1"} />
    </motion.div>
  );
};
